import * as React from 'react'
import { Link } from '@amzn/dvui/dist/Link'
import { MessageBlock, MessageTypes } from '@amzn/dvui/dist/Block'
import { Spinner } from '@amzn/dvui/dist/Spinner'
import { GetGiftCardInput, RequestContext } from '@utils'
import * as st from './giftCardModal.module.scss'
import { TextLocalizer } from '../localizer/localizer'
import { StringId } from '../localizer/stringId'
import { BalancesTable } from './balancesTable'
import { CodeSubmissionForm, videoHelpUrl } from './codeSubmissionForm'
import * as Types from 'DVWebNodeWpsRuntime'
import BalanceCodeType = Types.atv.wps.BalanceCodeType
import GetGiftCardOutputProps = Types.atv.wps.GetGiftCardOutputProps
import GiftCardBalanceList = Types.atv.wps.GiftCardBalanceList
import GiftCardBalance = Types.atv.wps.GiftCardBalance
import { ILogger, withLogger } from '../../context/withLogger'
import { wpsClient } from '../../utils/wps'

const LOG_PREFIX = 'DVWebNodeDetail-GetGiftCard'

const learnMoreUrl = '/help/video/giftcard'

const updateFailedStringAttrs = {
  url: {
    href: videoHelpUrl
  }
}

interface IGiftCardModalContentProps {
  requestContext: RequestContext
}

type GiftCardModalContentProps = IGiftCardModalContentProps & ILogger

interface GiftCardModalContentState {
  balanceStatus: BalanceStatus
  giftCardContent?: GiftCardContent
}

interface GiftCardContent {
  csrfToken: string;
  balanceMap: BalanceMap;
}

export type BalanceMap = {
  [key in BalanceCodeType]?: GiftCardBalance;
}

export const enum BalanceStatus {
  Ready,
  Loading,
  Failed
}

export class GiftCardModalContentWithLogger extends React.Component<GiftCardModalContentProps, GiftCardModalContentState> {

  constructor(props: GiftCardModalContentProps) {
    super(props)
    this.state = {
      balanceStatus: BalanceStatus.Loading
    }
  }

  componentDidMount() {
    this.updateBalance(['GiftCard', 'Promotion'])
  }

  updateBalance = async (balanceTypeList: BalanceCodeType[]) => {
    try {
      const request: GetGiftCardInput = {
        context: this.props.requestContext,
        balanceTypes: balanceTypeList
      }
      this.setState({ balanceStatus: BalanceStatus.Loading })
      const response: GetGiftCardOutputProps = await wpsClient.GetGiftCard(request)

      const { giftCardContent } = this.state
      const balanceMap = mergeBalances(giftCardContent && giftCardContent.balanceMap, response && response.balances)

      this.setState({
        balanceStatus: BalanceStatus.Ready,
        giftCardContent: {
          csrfToken: response && response.csrfToken,
          balanceMap: balanceMap
        }
      })
    } catch (e) {
      this.props.logger
        .error(`${LOG_PREFIX} - GetGiftCard request failed`, e)
        .count(LOG_PREFIX, 1, true)
      this.setState({
        balanceStatus: BalanceStatus.Failed,
        giftCardContent: undefined
      })
    }
  }

  render() {
    const { requestContext } = this.props
    const { giftCardContent, balanceStatus } = this.state

    return (
      <div>
        <CodeSubmissionForm csrfToken={giftCardContent && giftCardContent.csrfToken}
                            requestContext={requestContext}
                            updateBalance={this.updateBalance}
                            hideInput={balanceStatus === BalanceStatus.Failed}
                            />

        <p className={st.promotionMessage}>
          <TextLocalizer stringID={StringId.AVOD_DP_GC_promotion_message}/> <Link href={learnMoreUrl} target={'_blank'}>
          <TextLocalizer stringID={StringId.AVOD_DP_GC_toc_learn_more}/>
        </Link>
        </p>

        <h3 className={st.balancesTitle}><TextLocalizer stringID={StringId.DV_DP_DV_GCPC_window_title}/></h3>

        {balanceStatus !== BalanceStatus.Failed &&
          <p className={st.balancesExplanation}>
            <TextLocalizer stringID={StringId.DV_DP_GC_balances_explanation}/>
          </p>
        }

        <div className={st.giftCardModalContentStatusBlock}>
          <GiftCardModalContentStatusBlock balanceStatus={balanceStatus} balances={giftCardContent && giftCardContent.balanceMap && Object.values(giftCardContent.balanceMap)}/>
        </div>
      </div>
    )
  }
}

export const GiftCardModalContent = withLogger(GiftCardModalContentWithLogger)

interface GiftCardModalContentStatusBlockProps {
  balanceStatus: BalanceStatus
  balances: GiftCardBalanceList
}

const blockProps = { className: st.giftCardModalMessageBlock, heading: '', dismissable: false }

export const GiftCardModalContentStatusBlock = ({ balanceStatus, balances }: GiftCardModalContentStatusBlockProps) => {
  switch (balanceStatus) {
    case BalanceStatus.Ready:
      return (<BalancesTable balances={balances}/>)
    case BalanceStatus.Loading:
      return (<Spinner/>)
    case BalanceStatus.Failed:
      return (
        <div id={'balanceUpdateFailedBlock'}>
          <MessageBlock type={MessageTypes.warning} {...blockProps}>
            <TextLocalizer stringID={StringId.DV_DP_GC_balance_update_failed}
                           dvStringAttrs={updateFailedStringAttrs}/>
          </MessageBlock>
        </div>)
    default:
      return null
  }
}

export const mergeBalances = (existingMap: BalanceMap, newBalances: GiftCardBalance[]): BalanceMap => {
  const newMap: BalanceMap = {...existingMap}
  newBalances && newBalances.forEach( ( balance ) => newMap[balance.balanceType] = balance)
  return newMap
}